import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { ObjectReadGuard } from "@/ui/ObjectReadGuard";
import { LEVEL_LABEL, STAGE_LABEL } from "@/ui/vocabulary";
import { InterviewerRoom } from "@/views/debriefs/InterviewerRoom";
import { WrittenInputDialog } from "@/views/debriefs/WrittenInputDialog";
import { ANSWER_SAID, ATTENDANCE_TONE, hireLikeOf, offeredOf, recoLabel, recoTone, STAGE_LEGEND, STATUS_LABEL, STATUS_TONE } from "@/views/debriefs/vocabulary";
import { NotFound } from "@/views/NotFound";
import { FileField } from "@/ui/FileField";
import { SavedPrint, type SavedRendition } from "@/ui/SavedPrint";
import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  candidateList,
  debriefAdvanceAgenda,
  debriefCancel,
  debriefClose,
  debriefConfirmAttendance,
  debriefConfirmOutcome,
  debriefCountersignReversal,
  debriefCreate,
  debriefDeclineAttendance,
  debriefListMine,
  debriefOpen,
  debriefRecordAttendance,
  debriefRecordHrbpNote,
  debriefRecordOutcome,
  debriefRecordReferenceDecision,
  debriefResolveDivergence,
  debriefReveal,
  debriefReverseOutcome,
  debriefScheduleAdditionalRound,
  debriefSetAlignment,
  debriefSubmitWrittenInput,
  debriefWithdrawReversal,
  delegationList,
  dispositionOpenWorklist,
  summaryCompile,
  summaryExport,
  summaryOpen,
  summaryReadinessCheck,
  summaryRecordException,
  type DebriefOpenOutput,
} from "@/api/tools";
import { AppError, businessLocalNow, businessLocalToISO } from "@/api/runtime";
import { isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { McPage } from "@/shell/page";
import { DebriefSchedule } from "@/views/scheduling/DebriefSchedule";
import { ScheduleStandalone } from "@/views/scheduling/ScheduleSection";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NA, Banner, Dialog, Empty, Field, KV, Pill, ReasonDialog, Receipt, Skeleton, Table, fmtDate } from "@/ui";
import { CardGrid, ITINERARY_LABELS, NAV_ITEM_LABELS, RecordCard, SURFACE_LABELS } from "@/mc";
import { MEETING_TEMPLATE_LABELS } from "@/views/scheduling/labels";

// V1-INTERACTIONS §4.14 (Summary Sheet) and §4.15 (Debrief): `#debriefs` (per role), `#requisitions/<R>/debriefs` (the
// requisition's cycles, New cycle), `#requisitions/<R>/debriefs/<dc>` (the room), `#requisitions/<R>/summary/<cand>`
// (and `/<dc>` for one cycle's sheet).

const VALUE_NAME: Record<string, string> = { respect: "Respect", ownership: "Ownership", opportunity: "Opportunity", together: "Together", service: "Service" };
const AGENDA_LABEL: Record<string, string> = { compiled_scorecards: "Compiled scorecards", strengths_and_gaps: "Strengths and gaps", averages_and_recommendations: "Averages and each recommendation", alignment: "Alignment", reference_check: "Reference check", decision: "Decision" };
const ROLE_LABEL: Record<string, string> = { recruiter: "Recruiter", hm: "Hiring Manager", hrbp: "HR Business Partner", interviewer: "Interviewer", admin: "Admin", facilitator: "Facilitator", participant: "Participant" };
// the roll and the invitation answer are two different things (Codex #7 M05): only these three are roll states.
const ROLL_STATES = ["present", "absent_with_written_input", "absent"] as const;
const ROLL_LABEL: Record<string, string> = { present: "Present", absent_with_written_input: "Absent — written input read in", absent: "Absent" };
const OUTCOME_LABEL: Record<string, string> = { extend_offer: "Extend an offer", hold_as_alternate: "Hold as alternate", additional_round: "Run an additional round", pass: "Pass" };
const SOURCE_LABEL: Record<string, string> = { paper: "on paper", verbal: "in person", call: "by telephone", mail: "by mail" };
const VALUES = ["respect", "ownership", "opportunity", "together", "service"] as const;
const gapText = (g: string) => g.replace(/^[a-z0-9_]+:\s*/, "").replace(/^\w/, (c) => c.toUpperCase());
// one canonical dimension identifier, the snapshot's own: `skill_<n>` for a skill, the value's key for a value (M07).
const dimName = (d: string) => (d.startsWith("skill_") ? `Skill ${d.slice(6)}` : (VALUE_NAME[d] ?? d));
const dimShort = (d: string) => (d.startsWith("skill_") ? `S${d.slice(6)}` : (VALUE_NAME[d] ?? d).slice(0, 3));
const EXCEPTIONS: [string, string][] = [
  ["scorecard_missing_at_reveal", "Scorecard missing at reveal"],
  ["round_not_held_at_reveal", "Round not held at reveal"],
  ["coverage_gap_at_reveal", "Coverage gap at reveal"],
  ["debrief_started_early", "Debrief started early"],
  ["application_excluded_from_cycle", "Application excluded from the cycle"],
  ["exercise_waived", "Exercise waived"],
  ["checkpoint_skipped", "Checkpoint skipped"],
  ["intake_finalized_with_absent_party", "Intake finalized with an absent party"],
  ["hold_resumed_with_changes", "Hold resumed with changes"],
  ["reference_gate_waived", "Reference gate waived"],
];

// ---- #debriefs — my debriefs -------------------------------------------------------------------------------------------------------

type DebriefRow = NonNullable<Awaited<ReturnType<typeof debriefListMine>>>["items"][number];

//: AF-2 (EM-2 + CALL-17 21:58, fork F1): the level bands in the order the Process Guide names them.
const LEVEL_ORDER = ["manager_and_below", "director_srdirector", "vp_and_above"];

/** The interviewer's two replies to a debrief invitation (F-85): the reply they gave is pressed and drawn as the selected
 *  button, and the other stays offered so the answer can be changed; before any answer neither is pressed. */
export function ReplyButtons({ reply, busy, small = false, onAttend, onDecline }: { reply: string | null | undefined; busy: boolean; small?: boolean; onAttend: () => void; onDecline: () => void }) {
  const cls = (chosen: boolean) => ["btn", small ? "btn--sm" : "", chosen ? "btn--primary" : ""].filter(Boolean).join(" ");
  return (
    <>
      <button type="button" className={cls(reply === "confirmed")} aria-pressed={reply === "confirmed"} disabled={busy} onClick={onAttend}>
        Attend
      </button>
      <button type="button" className={cls(reply === "declined")} aria-pressed={reply === "declined"} onClick={onDecline}>
        I cannot attend
      </button>
    </>
  );
}

/** `#debriefs` (every cycle on the reader's list) and a requisition's cycles. `hosted` (F-186): the requisition record's
 * Debriefs tab, under the record's own header and tabs — no heading of its own, and the cycles as cards in the record's
 * grid: one card for the tab's controls (New cycle, the Stage filter and its legend), then one card per cycle. */
export function DebriefsHome({ rid, hosted = false }: { rid?: string | undefined; hosted?: boolean | undefined }) {
  const { register } = useViewState();
  const { can } = useSession();
  const q = useRead(["debriefs", rid ?? "all"], () => debriefListMine(rid ? { requisition_id: rid } : {}));
  const create = useWrite((i: Parameters<typeof debriefCreate>[0], k) => debriefCreate(i, k), [["debriefs"], ["requisition", rid ?? ""], ["today"]]);
  useEffect(() => register({ view: "debriefs", ids: rid ? { requisition: rid } : {}, summary: `debriefs ${q.data?.items.length ?? "?"}` }), [register, rid, q.data?.items.length]);
  const confirm = useWrite((i: { requisition_id: string; cycle_id: string }, k) => debriefConfirmAttendance(i, k), [["debriefs"], ["today"]]);
  const decline = useWrite((i: { requisition_id: string; cycle_id: string; reason: string }, k) => debriefDeclineAttendance(i, k), [["debriefs"], ["today"]]);
  const [dlg, setDlg] = useState<null | { rid: string; cid: string }>(null);
  const [newDlg, setNewDlg] = useState(false);
  const [stage, setStage] = useState("");
  const all = q.data?.items ?? [];
  const items = stage ? all.filter((c) => c.status === stage) : all;
  // the stages actually on this list, in the order the cycle moves through them
  const stages = ["created", "scheduled", "revealed", "decided", "closed", "cancelled"].filter((v) => all.some((c) => c.status === v));
  // AF-2, fork F1: level band (the email) with a count, then the role — the requisition — inside it (the call). One
  // requisition's own debriefs page is one role by definition, so it keeps the single table it always had.
  const bands = [...LEVEL_ORDER, ""].map((level) => ({ level, rows: items.filter((c) => (level ? c.level === level : !LEVEL_ORDER.includes(c.level))) })).filter((b) => b.rows.length > 0);
  const head = (
    <tr>
      <th className="sticky-l">Requisition</th>
      <th>Stage</th>
      <th>When</th>
      <th>Candidates</th>
      <th>Ownership</th>
      <th>Attendance</th>
      <th className="sticky-r">Action</th>
    </tr>
  );
  const row = (c: DebriefRow) => (
    <tr key={c.cycle_id}>
      <td className="sticky-l">
        <span className="mono">{c.requisition_id}</span> {c.requisition_title}
      </td>
      <td>
        #{c.sequence} <Pill tone={STATUS_TONE[c.status] ?? "muted"}>{STATUS_LABEL[c.status] ?? c.status}</Pill>
      </td>
      <td>{c.start ? fmtDate(c.start, true) : <span className="muted">not scheduled</span>}</td>
      <td>{c.candidates.map((candidate, i) => <div key={i} className={c.candidates_named ? undefined : "mono"}>{candidate}</div>)}{!c.candidates_named && <div className="muted">Names appear at the reveal.</div>}</td>
      <td>
        {ROLE_LABEL[c.my_role] ?? c.my_role}
        {c.my_scorecards_due ? <Pill tone="warn">{c.my_scorecards_due} scorecard due</Pill> : null}
      </td>
      {/* AF-19 (EM-17): three values, derived by the server. Answering the invitation is a different fact from
          attending the call, so the answer is said beside the column and never inside it. */}
      <td>
        <Pill tone={ATTENDANCE_TONE[c.my_attendance_label] ?? "muted"}>{c.my_attendance_label}</Pill>
        {c.my_attendance && ANSWER_SAID[c.my_attendance] ? <div className="muted">You replied: {ANSWER_SAID[c.my_attendance]}</div> : null}
      </td>
      <td className="sticky-r">
        <div className="row" style={{ gap: 6 }}>
          <a className="btn btn--sm btn--primary" href={`#requisitions/${c.requisition_id}/debriefs/${c.cycle_id}`}>
            Open
          </a>
          {c.my_attendance === "declined" && c.status === "scheduled" && can("debrief.submit_written_input") && <div><a className="btn btn--sm" href={`#requisitions/${c.requisition_id}/debriefs/${c.cycle_id}`}>Written input</a><div className="muted">Due by {c.start ? fmtDate(c.start, true) : "the debrief start"}</div></div>}
          {c.status === "scheduled" && c.my_role !== "facilitator" && can("debrief.confirm_attendance") && (
            <ReplyButtons small reply={c.my_attendance} busy={confirm.isPending} onAttend={() => void confirm.mutateAsync({ requisition_id: c.requisition_id, cycle_id: c.cycle_id })} onDecline={() => setDlg({ rid: c.requisition_id, cid: c.cycle_id })} />
          )}
        </div>
      </td>
    </tr>
  );
  const newCycle = rid && can("debrief.create") ? <button className="btn btn--primary" onClick={() => setNewDlg(true)}>New cycle</button> : null;
  const stageFilter = all.length > 0 && stages.length > 1 && (
    <div className="filter-row">
      <label className="filter">
        <span>Stage</span>
        <select value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="">Any stage</option>
          {stages.map((v) => (
            <option key={v} value={v}>
              {STATUS_LABEL[v] ?? v}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
  const dialogs = (
    <>
      {rid && <NewCycleDialog rid={rid} open={newDlg} onClose={() => setNewDlg(false)} create={create} />}
      <ReasonDialog title="I cannot attend" label="Why" confirm="Decline" open={!!dlg} onClose={() => setDlg(null)} busy={decline.isPending} error={decline.error} onConfirm={(reason) => dlg && void decline.mutateAsync({ requisition_id: dlg.rid, cycle_id: dlg.cid, reason }).then(() => setDlg(null))}>
        <p className="muted">Your written input (alignment and notes) is due before the debrief starts and is read into the room.</p>
      </ReasonDialog>
    </>
  );
  if (hosted && rid) {
    // F-186: the tab's controls in one card, each cycle in a card of its own (what it is, where it stands, who owns it,
    // when it is, what you can do), in the record's grid
    return (
      <>
        <CardGrid>
          <RecordCard variant="form" span="all" icon="users" title={NAV_ITEM_LABELS.debriefs} count={q.data ? all.length : undefined} action={all.length > 0 ? newCycle ?? undefined : undefined}>
            {all.length > 0 && <p className="muted">{STAGE_LEGEND}</p>}
            <Banner error={create.error ?? confirm.error ?? decline.error} />
            {create.isSuccess && <Receipt>Cycle created — the sheets compiled sealed. Schedule the room next.</Receipt>}
            {q.isLoading && <Skeleton />}
            {q.isError && <Banner error={q.error} onRetry={() => void q.refetch()} />}
            {q.data && all.length === 0 && <Empty level={2} heading="No cycle yet" action={newCycle ?? undefined}>New cycle takes every panel candidate with a complete loop.</Empty>}
            {stageFilter}
            {all.length > 0 && items.length === 0 && <Empty heading="Nothing at that stage">Clear the Stage filter to see every debrief on your list.</Empty>}
          </RecordCard>
          {items.map((c) => (
            <RecordCard
              key={c.cycle_id}
              variant="summary"
              icon="users"
              title={`${MEETING_TEMPLATE_LABELS.debrief} #${c.sequence}`}
              pills={<Pill tone={STATUS_TONE[c.status] ?? "muted"}>{STATUS_LABEL[c.status] ?? c.status}</Pill>}
              sub={ROLE_LABEL[c.my_role] ?? c.my_role}
              rows={[
                [ITINERARY_LABELS.when, c.start ? fmtDate(c.start, true) : <span className="muted">not scheduled</span>],
                [NAV_ITEM_LABELS.candidates, <>{c.candidates.map((candidate, i) => <div key={i} className={c.candidates_named ? undefined : "mono"}>{candidate}</div>)}{!c.candidates_named && <div className="muted">Names appear at the reveal.</div>}</>],
                [SURFACE_LABELS.attendanceRoll, <><Pill tone={ATTENDANCE_TONE[c.my_attendance_label] ?? "muted"}>{c.my_attendance_label}</Pill>{c.my_attendance && ANSWER_SAID[c.my_attendance] ? <div className="muted">You replied: {ANSWER_SAID[c.my_attendance]}</div> : null}</>],
              ]}
              action={<a className="btn btn--sm btn--primary" href={`#requisitions/${c.requisition_id}/debriefs/${c.cycle_id}`}>Open</a>}
            >
              {c.my_scorecards_due ? <div><Pill tone="warn">{c.my_scorecards_due} scorecard due</Pill></div> : null}
              {c.my_attendance === "declined" && c.status === "scheduled" && can("debrief.submit_written_input") && <div><a className="btn btn--sm" href={`#requisitions/${c.requisition_id}/debriefs/${c.cycle_id}`}>Written input</a><div className="muted">Due by {c.start ? fmtDate(c.start, true) : "the debrief start"}</div></div>}
              {c.status === "scheduled" && c.my_role !== "facilitator" && can("debrief.confirm_attendance") && (
                <ReplyButtons small reply={c.my_attendance} busy={confirm.isPending} onAttend={() => void confirm.mutateAsync({ requisition_id: c.requisition_id, cycle_id: c.cycle_id })} onDecline={() => setDlg({ rid: c.requisition_id, cid: c.cycle_id })} />
              )}
            </RecordCard>
          ))}
        </CardGrid>
        {dialogs}
      </>
    );
  }
  const byRequisition = (rows: DebriefRow[]) => {
    const map = new Map<string, DebriefRow[]>();
    for (const c of rows) map.set(c.requisition_id, [...(map.get(c.requisition_id) ?? []), c]);
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  };
  return (
    <div>
      <div className="hrow">
        <div>
          <h1 className="page">{rid ? `Debriefs · ${rid}` : "My Debriefs"}</h1>
          <p className="lede">{rid ? "One room per cycle: the roll, the reveal, alignment per participant, the outcome." : "The debriefs you facilitate, attend or hold a scorecard on. Candidates stay tokens until the reveal."}</p>
        </div>
        {!!q.data?.items.length && newCycle}
      </div>
      {/* AF-17 (EM-15): Decided and Closed said plainly, on the screen, beside the column that carries them. */}
      {all.length > 0 && <p className="muted">{STAGE_LEGEND}</p>}
      <Banner error={create.error ?? confirm.error ?? decline.error} />
      {create.isSuccess && <Receipt>Cycle created — the sheets compiled sealed. Schedule the room next.</Receipt>}
      {q.isLoading && <Skeleton />}
      {q.isError && <Banner error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && all.length === 0 && <Empty level={2} heading={rid ? "No cycle yet" : "No debrief on your list"} action={rid && can("debrief.create") ? <button className="btn btn--primary" onClick={() => setNewDlg(true)}>New cycle</button> : undefined}>{rid ? "New cycle takes every panel candidate with a complete loop." : "Your invitations will appear here."}</Empty>}
      {stageFilter}
      {all.length > 0 && items.length === 0 && <Empty heading="Nothing at that stage">Clear the Stage filter to see every debrief on your list.</Empty>}
      {rid && items.length > 0 && (
        <Table label="Debriefs" head={head}>
          {items.map(row)}
        </Table>
      )}
      {!rid &&
        bands.map((b) => (
          <section key={b.level || "other"}>
            <h2 className="sec">
              {LEVEL_LABEL[b.level] ?? "Other levels"} <span className="muted">· {b.rows.length} debrief{b.rows.length === 1 ? "" : "s"}</span>
            </h2>
            {byRequisition(b.rows).map(([reqId, rows]) => (
              <div key={reqId}>
                <h3 className="sub">
                  <span className="mono">{reqId}</span> {rows[0]!.requisition_title} <span className="muted">· {rows.length} debrief{rows.length === 1 ? "" : "s"}</span>
                </h3>
                <Table label={`Debriefs · ${reqId}`} head={head}>
                  {rows.map(row)}
                </Table>
              </div>
            ))}
          </section>
        ))}
      {dialogs}
    </div>
  );
}

// The cycle's membership, and the two exceptions the create call accepts: the room started before a loop finished, and a
// candidate with a complete loop deliberately left out (Codex #7 M03).
function NewCycleDialog({ rid, open, onClose, create }: { rid: string; open: boolean; onClose: () => void; create: ReturnType<typeof useWrite<Parameters<typeof debriefCreate>[0], unknown>> }) {
  const pool = useRead(["candidates", rid, "panel"], () => candidateList({ requisition_id: rid, stage: "panel" }), { enabled: open });
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [why, setWhy] = useState<Record<string, string>>({});
  const [early, setEarly] = useState("");
  useEffect(() => {
    if (!open) {
      setPicked({});
      setWhy({});
      setEarly("");
    }
  }, [open]);
  const items = pool.data?.items ?? [];
  const inCycle = (id: string) => picked[id] !== false;
  const left = items.filter((i) => !inCycle(i.id));
  const chosen = items.filter((i) => inCycle(i.id));
  const missingReason = left.some((i) => (why[i.id] ?? "").trim().length < 3);
  const submit = () => {
    const input: Parameters<typeof debriefCreate>[0] = left.length
      ? { requisition_id: rid, application_ids: chosen.map((i) => i.id), excluded: left.map((i) => ({ application_id: i.id, reason: (why[i.id] ?? "").trim() })) }
      : { requisition_id: rid };
    if (early.trim().length >= 3) input.early_start_reason = early.trim();
    void create.mutateAsync(input).then(() => onClose());
  };
  return (
    <Dialog title="New debrief cycle" open={open} onClose={onClose}>
      <p className="muted">The cycle freezes its membership and its evidence when the room opens. Leave every candidate ticked to take the default — every panel candidate whose loop is complete.</p>
      {pool.isLoading && <Skeleton rows={3} />}
      {pool.data && items.length === 0 && <Empty heading="No panel candidates yet">No candidate is at the panel stage on this requisition.</Empty>}
      {items.length > 0 && (
        <ul className="plain members">
          {items.map((i) => (
            <li key={i.id} className="member">
              <label className="check">
                <input type="checkbox" checked={inCycle(i.id)} onChange={(e) => setPicked({ ...picked, [i.id]: e.target.checked })} /> {("display_name" in i && typeof i.display_name === "string" ? i.display_name : null) ?? i.token} <span className="muted">{i.stage_label}</span>
              </label>
              {!inCycle(i.id) && (
                <Field label={`Why ${("display_name" in i && typeof i.display_name === "string" ? i.display_name : null) ?? i.token} is left out`} required id={`ex-${i.id}`} hint="A candidate whose loop is complete needs this reason on the record.">
                  <input id={`ex-${i.id}`} type="text" value={why[i.id] ?? ""} onChange={(e) => setWhy({ ...why, [i.id]: e.target.value })} />
                </Field>
              )}
            </li>
          ))}
        </ul>
      )}
      <Field label="Why the room starts early" id="early-reason" hint="Only when a candidate you have ticked has not finished their loop.">
        <textarea id="early-reason" rows={2} value={early} onChange={(e) => setEarly(e.target.value)} />
      </Field>
      <Banner error={create.error} />
      <div className="acts">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn--primary" disabled={create.isPending || missingReason || chosen.length === 0} onClick={submit}>
          Create the cycle
        </button>
      </div>
    </Dialog>
  );
}

// ---- the room ------------------------------------------------------------------------------------------------------------------------

// V1-INTERACTIONS §4.15 — one route, two rooms. `debrief.open` says which projection the reader was served, so the
// screen never guesses a seat from a role: `interviewer` opens the descoped room (AF-20…AF-23) and everyone else the
// room they have always had. The narrowing is the server's; this only chooses which of the two screens renders it.
export function DebriefRoom({ rid, cid }: { rid: string; cid: string }) {
  const { register } = useViewState();
  const q = useRead(["debrief", cid, rid], () => debriefOpen({ requisition_id: rid, cycle_id: cid }));
  useEffect(() => register({ view: "debrief", ids: { requisition: rid, cycle: cid }, summary: `debrief ${q.data?.status ?? ""}` }), [register, rid, cid, q.data?.status]);
  // the Coordinator schedules the debrief but reads none of the room ("Coordinator schedules but has no vote."): the
  // debrief's scheduling card, with its readiness, is their page
  if (q.error && isClientRefusal(q.error)) {
    return <ScheduleStandalone subject={{ kind: "debrief", rid, cid }} route={`requisitions/${rid}/debriefs/${cid}`} body={<DebriefSchedule rid={rid} cid={cid} />} />;
  }
  if (q.error || !q.data) return <ObjectReadGuard query={q} />;
  return q.data.projection === "interviewer" ? <InterviewerRoom rid={rid} cid={cid} /> : <FacilitatorRoom rid={rid} cid={cid} />;
}

function FacilitatorRoom({ rid, cid }: { rid: string; cid: string }) {
  const queryClient = useQueryClient();
  const { me, can } = useSession();
  const q = useRead(["debrief", cid, rid], () => debriefOpen({ requisition_id: rid, cycle_id: cid }));
  // who may sit in for a seat holder on the roll — the arrangements the server says exist on this requisition (§4.20)
  const standIns = useRead(["delegations", rid], () => delegationList({ requisition_id: rid }), { retry: false, enabled: can("delegation.list") });
  // §4.17: a pass carries a reason code the person picked from the configured dictionary — never one the screen invents.
  // The dictionary is the one the disposition worklist already publishes, because that is where the code is carried to.
  const reasonCodes = useRead(["disposition-codes", rid], () => dispositionOpenWorklist({ requisition_id: rid }), { retry: false, enabled: can("disposition.open_worklist") });
  const inval = [["debrief", cid], ["debriefs"], ["summary"], ["application"], ["candidates", rid], ["today"]] as const;
  const cancel = useWrite((i: { requisition_id: string; cycle_id: string; reason: string }, k) => debriefCancel(i, k), inval);
  const roll = useWrite((i: Parameters<typeof debriefRecordAttendance>[0], k) => debriefRecordAttendance(i, k), inval);
  const reveal = useWrite((i: { requisition_id: string; cycle_id: string }, k) => debriefReveal(i, k), inval);
  const agenda = useWrite((i: Parameters<typeof debriefAdvanceAgenda>[0], k) => debriefAdvanceAgenda(i, k), inval);
  const resolve = useWrite((i: { requisition_id: string; cycle_id: string; divergence_id: string; resolution_note: string }, k) => debriefResolveDivergence(i, k), inval);
  const align = useWrite((i: Parameters<typeof debriefSetAlignment>[0], k) => debriefSetAlignment(i, k), inval);
  const note = useWrite((i: { requisition_id: string; cycle_id: string; application_id?: string | null; text: string }, k) => debriefRecordHrbpNote(i, k), inval);
  const reference = useWrite((i: Parameters<typeof debriefRecordReferenceDecision>[0], k) => debriefRecordReferenceDecision(i, k), inval);
  const outcome = useWrite((i: Parameters<typeof debriefRecordOutcome>[0], k) => debriefRecordOutcome(i, k), inval);
  const confirm = useWrite((i: { requisition_id: string; cycle_id: string; application_id: string }, k) => debriefConfirmOutcome(i, k), inval);
  const reverse = useWrite((i: Parameters<typeof debriefReverseOutcome>[0], k) => debriefReverseOutcome(i, k), inval);
  const countersign = useWrite((i: { requisition_id: string; cycle_id: string; application_id: string; told_check: boolean }, k) => debriefCountersignReversal(i, k), inval);
  const withdrawReversal = useWrite((i: { requisition_id: string; cycle_id: string; application_id: string; reason: string }, k) => debriefWithdrawReversal(i, k), inval);
  const written = useWrite((i: Parameters<typeof debriefSubmitWrittenInput>[0], k) => debriefSubmitWrittenInput(i, k), inval);
  const addRound = useWrite((i: Parameters<typeof debriefScheduleAdditionalRound>[0], k) => debriefScheduleAdditionalRound(i, k), inval);
  const close = useWrite((i: { requisition_id: string; cycle_id: string }, k) => debriefClose(i, k), inval);
  const [reversalFile, setReversalFile] = useState<string | null>(null);
  const [reversalBusy, setReversalBusy] = useState(false);
  const [rollState, setRollState] = useState<Record<string, string>>({});
  const [passCode, setPassCode] = useState("");
  const [told, setTold] = useState(false);
  const [dlg, setDlg] = useState<null | { kind: "cancel" } | { kind: "note"; aid: string | null } | { kind: "reference"; aid: string; elected: boolean } | { kind: "outcome"; aid: string; value: "extend_offer" | "hold_as_alternate" | "additional_round" | "pass" } | { kind: "divergence"; id: string; label: string } | { kind: "recordFor"; aid: string } | { kind: "reverse"; aid: string } | { kind: "countersign"; aid: string } | { kind: "withdrawReversal"; aid: string } | { kind: "written"; aid: string; candidate: string } | { kind: "addRound"; aid: string }>(null);
  if (q.error || !q.data) return <ObjectReadGuard query={q} />;
  const c = q.data;
  const err = cancel.error ?? roll.error ?? reveal.error ?? agenda.error ?? resolve.error ?? align.error ?? note.error ?? reference.error ?? outcome.error ?? confirm.error ?? reverse.error ?? withdrawReversal.error ?? written.error ?? addRound.error ?? close.error;
  const revealed = c.status === "revealed" || c.status === "decided" || c.status === "closed";
  const codeOptions = (reasonCodes.data?.codes ?? []).map((x) => [String(x["slug"]), String(x["label"] ?? x["slug"])] as [string, string]);
  // who the Hiring Manager is on this room comes from the roll the server returns, never from a role the browser guesses
  const hmRow = c.required_attendees.find((a) => String(a["role"]) === "hm");
  const hmId = hmRow ? String(hmRow["person_id"]) : "";
  const hmName = hmRow ? String(hmRow["name"]) : "the Hiring Manager";
  const meRow = c.required_attendees.find((a) => String(a["person_id"]) === me?.person_id);
  const iDeclined = String(meRow?.["status"] ?? "") === "declined";
  const canRecordFor = can("debrief.record_outcome") && !!hmId && me?.person_id !== hmId;
  const covers = new Map<string, { id: string; grantee_id: string; grantee_name: string }[]>();
  for (const d of standIns.data?.delegations ?? []) {
    if (!d.live || d.status !== "active" || !d.capabilities.includes("attend")) continue;
    covers.set(d.grantor_id, [...(covers.get(d.grantor_id) ?? []), { id: d.id, grantee_id: d.grantee_id, grantee_name: d.grantee_name }]);
  }
  return (
    <div><RecordAfterFact id={rid} family="requisition" section="debriefs" />
      <div className="hrow">
        <div>
          <h1 className="page">
            Debrief #{c.sequence} · {c.requisition_title}
          </h1>
          <p className="lede">
            {/* N02: the requisition's own cycle list belongs to the seats that may open the requisition. AF-1 closed
                that door for the interviewer, so their way back is their own debriefs, never a link that refuses. */}
            <a href={can("requisition.open") ? `#requisitions/${rid}/debriefs` : "#debriefs"}>← debriefs</a> · facilitator {c.facilitator} · {c.kind.replace(/_/g, " ")}
            {c.booking ? ` · ${fmtDate(String(c.booking["start"]), true)}` : " · not scheduled"}
          </p>
        </div>
        <div className="acts">
          <Pill tone={STATUS_TONE[c.status] ?? "muted"}>{STATUS_LABEL[c.status] ?? c.status}</Pill>
          {c.can["reveal"] && (
            <button className="btn btn--primary" disabled={reveal.isPending} onClick={() => void reveal.mutateAsync({ requisition_id: rid, cycle_id: cid })}>
              Reveal the Summary Sheets
            </button>
          )}
          {c.can["close"] && (
            <button className="btn btn--primary" disabled={close.isPending} onClick={() => void close.mutateAsync({ requisition_id: rid, cycle_id: cid })}>
              Close the debrief
            </button>
          )}
          {c.can["cancel"] && (
            <button className="btn" onClick={() => setDlg({ kind: "cancel" })}>
              Cancel cycle
            </button>
          )}
          {c.can["record_hrbp_note"] && (
            <button className="btn" onClick={() => setDlg({ kind: "note", aid: null })}>
              HR note on this debrief
            </button>
          )}
        </div>
      </div>
      <Banner error={err} />
      {reveal.isSuccess && <Receipt>Revealed — membership and evidence are frozen; the sheets are open to the room.</Receipt>}
      {!revealed && c.reveal_gaps.length > 0 && (
        <section className="card">
          <h2 className="sec">Before the reveal</h2>
          <ul>
            {c.reveal_gaps.map((g) => (
              <li key={g}>{gapText(g)}</li>
            ))}
          </ul>
        </section>
      )}
      {/* the debrief is scheduled on B5's card (S12): readiness, Schedule, the Outlook event and the responses */}
      <McPage>
        <DebriefSchedule rid={rid} cid={cid} />
      </McPage>
      <div className="grid grid--2">
        <section className="card">
          <h2 className="sec">Roll</h2>
          <ul className="plain roll">
            {c.required_attendees.map((a) => {
              const pid = String(a["person_id"]);
              const st = a["status"] ? String(a["status"]) : "";
              const isRoll = (ROLL_STATES as readonly string[]).includes(st);
              // the invitation ANSWER, said in the same words the three-value Attendance column says them (AF-19)
              const answer = ANSWER_SAID[st];
              const hasInput = !!a["has_written_input"];
              // the roll reads "written input read in" from the input's own record: a text-only input has no file, so
              // the stored status stays `absent` and the label comes from the record instead (Codex #7 M04)
              const rollLabel = isRoll ? (st === "absent" && hasInput ? ROLL_LABEL["absent_with_written_input"] : ROLL_LABEL[st]) : "not marked";
              const inputs = (a["written_input"] as { text?: string; alignment?: string | null; at?: string; file_id?: string; version?: number }[] | undefined) ?? [];
              return (
                <li key={pid} className="roll__row">
                  <div>
                    <b>{String(a["name"])}</b>{pid === me?.person_id && <span className="muted"> (you)</span>}
                    <div className="muted">{ROLE_LABEL[String(a["role"])] ?? String(a["role"])}</div>
                  </div>
                  {c.can["record_attendance"] ? (
                    <select className="roll__select" aria-label={`Attendance: ${String(a["name"])}`} value={rollState[pid] ?? (a["stood_in_by"] ? `standin:${(covers.get(pid) ?? [])[0]?.grantee_id ?? ""}` : isRoll ? st : "")} onChange={(e) => setRollState({ ...rollState, [pid]: e.target.value })}>
                      <option value="">—</option>
                      {(covers.get(pid) ?? []).map((sd) => (
                        <option key={sd.id} value={`standin:${sd.grantee_id}`}>
                          Present — {sd.grantee_name}
                        </option>
                      ))}
                      {ROLL_STATES.map((v) => {
                        // written input is a file on the record, not a claim the facilitator can make: the room only
                        // offers that state to someone whose written input is already in, and says so otherwise.
                        const noWritten = v === "absent_with_written_input" && !hasInput && st !== v;
                        return (
                          <option key={v} value={v} disabled={noWritten}>
                            {noWritten ? `${ROLL_LABEL[v]} — no written input on record` : ROLL_LABEL[v]}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <Pill tone={st === "present" ? "ok" : isRoll ? "warn" : "muted"}>{rollLabel}</Pill>
                  )}
                  <span className="muted">{[answer ? `replied ${answer}` : "", hasInput ? "written input in" : "", a["acting_for"] ? `for ${String(a["acting_for"])}` : "", a["stood_in_by"] ? `covered by ${String(a["stood_in_by"])}` : ""].filter(Boolean).join(" · ")}</span>
                  {inputs.map((w, i) => (
                    <p key={i} className="roll__input">
                      {w.alignment ? `${recoLabel(w.alignment) ?? w.alignment} — ` : ""}
                      {w.text}
                      {w.file_id && <span className="file-field__hash">Read in from <a href={`/api/files/${encodeURIComponent(w.file_id)}`} download>the written input document</a>{w.version != null ? ` (v${w.version})` : ""}</span>}
                    </p>
                  ))}
                </li>
              );
            })}
          </ul>
          {c.can["record_attendance"] && (
            <button className="btn btn--sm btn--primary" disabled={roll.isPending || Object.keys(rollState).length === 0} onClick={() => void roll.mutateAsync({ requisition_id: rid, cycle_id: cid, attendance: Object.entries(rollState).filter(([, v]) => v).map(([person_id, v]) => (v.startsWith("standin:") ? { person_id: v.slice(8), status: "present", acting_for_id: person_id } : { person_id, status: v })) }).then((saved) => { queryClient.setQueryData(["debrief", cid, rid], saved); setRollState({}); })}>
              Take the roll
            </button>
          )}
        </section>
      </div>
      {revealed && (
        <section className="card">
          <h2 className="sec">Agenda <span className="muted" role="status" aria-live="polite">{c.agenda.filter(it => it["done"]).length} of {c.agenda.length} covered</span></h2>
          <ol className="agenda">
            {c.agenda.map((it) => (
              <li key={String(it["item"])}>
                {c.can["advance_agenda"] ? <label className="check agenda__item">
                  <input type="checkbox" checked={!!it["done"]} disabled={!!it["done"]} onChange={() => void agenda.mutateAsync({ requisition_id: rid, cycle_id: cid, item: String(it["item"]) as "decision" })} /> {AGENDA_LABEL[String(it["item"])] ?? String(it["item"])}
                </label> : <div className={`agenda__item${it["done"] ? "" : " muted"}`}><span className={it["done"] ? "text-ok" : "muted"} aria-hidden="true">{it["done"] ? "✓" : "○"}</span><span className="sr-only">{it["done"] ? "Covered: " : "Pending: "}</span>{AGENDA_LABEL[String(it["item"])] ?? String(it["item"])}</div>}
              </li>
            ))}
          </ol>
        </section>
      )}
      {c.members.map((m) => (
        <MemberCard
          key={m.application_id}
          rid={rid}
          cid={cid}
          m={m}
          revealed={revealed}
          hmName={hmName}
          offered={offeredOf(c)}
          hireLike={hireLikeOf(c)}
          canRecordFor={canRecordFor}
          canAddRound={can("debrief.schedule_additional_round")}
          canWrite={iDeclined && !revealed && can("debrief.submit_written_input")}
          busy={confirm.isPending || countersign.isPending}
          onAlign={(value) => void align.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: m.application_id, value: value as Parameters<typeof debriefSetAlignment>[0]["value"] })}
          onReference={(elected) => setDlg({ kind: "reference", aid: m.application_id, elected })}
          onOutcome={(value) => { setPassCode(""); setDlg({ kind: "outcome", aid: m.application_id, value }); }}
          onRecordFor={() => setDlg({ kind: "recordFor", aid: m.application_id })}
          onConfirm={() => void confirm.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: m.application_id })}
          onReverse={() => { setReversalFile(null); setDlg({ kind: "reverse", aid: m.application_id }); }}
          onCountersign={() => { setTold(false); setDlg({ kind: "countersign", aid: m.application_id }); }}
          onWithdrawReversal={() => setDlg({ kind: "withdrawReversal", aid: m.application_id })}
          onWritten={() => setDlg({ kind: "written", aid: m.application_id, candidate: m.candidate })}
          onAddRound={() => setDlg({ kind: "addRound", aid: m.application_id })}
          onNote={() => setDlg({ kind: "note", aid: m.application_id })}
          onDivergence={(id, label) => setDlg({ kind: "divergence", id, label })}
          canNote={!!c.can["record_hrbp_note"]}
          canResolve={!!c.can["resolve_divergence"]}
        />
      ))}
      {c.hrbp_notes.length > 0 && (
        <section className="card">
          <h2 className="sec">HR Business Partner notes</h2>
          <ul className="history">
            {c.hrbp_notes.map((n) => (
              <li key={String(n["id"])}>
                <div className="note__header"><span className="initial-badge" aria-hidden="true">{String(n["by"]).split(" ").map(word => word[0]).slice(0, 2).join("")}</span><b>{String(n["by"])}</b><time className="muted">{fmtDate(String(n["at"]), true)}</time></div>
                <div>{n["text"] ? String(n["text"]) : <i className="muted">a note exists</i>}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <ReasonDialog title="Cancel the cycle" label="Why" confirm="Cancel the cycle" open={dlg?.kind === "cancel"} onClose={() => setDlg(null)} busy={cancel.isPending} error={cancel.error} onConfirm={(reason) => void cancel.mutateAsync({ requisition_id: rid, cycle_id: cid, reason }).then(() => setDlg(null))} />
      <ReasonDialog title="HR Business Partner note" label="Note" confirm="Record" open={dlg?.kind === "note"} onClose={() => setDlg(null)} busy={note.isPending} error={note.error} onConfirm={(text) => dlg?.kind === "note" && void note.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, text }).then(() => setDlg(null))} />
      <ReasonDialog title={dlg?.kind === "reference" && dlg.elected ? "Reference check — yes" : "Reference check — no"} label={dlg?.kind === "reference" && dlg.elected ? "References to collect" : "Why not"} confirm="Record" open={dlg?.kind === "reference"} onClose={() => setDlg(null)} busy={reference.isPending} error={reference.error} onConfirm={(text) => dlg?.kind === "reference" && void reference.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, elected: dlg.elected, ...(dlg.elected ? { references_to_collect: text } : { reason_when_no: text }) }).then(() => setDlg(null))} />
      <ReasonDialog title={`Outcome — ${dlg?.kind === "outcome" ? (OUTCOME_LABEL[dlg.value] ?? dlg.value.replace(/_/g, " ")) : ""}`} label="Rationale" confirm="Record the outcome" open={dlg?.kind === "outcome"} onClose={() => setDlg(null)} busy={outcome.isPending} error={outcome.error} onConfirm={(rationale) => dlg?.kind === "outcome" && void outcome.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, outcome: dlg.value, rationale, ...(dlg.value === "pass" && passCode ? { reason_code: passCode } : {}) }).then(() => setDlg(null))}>
        {dlg?.kind === "outcome" && (dlg.value === "extend_offer" || dlg.value === "hold_as_alternate") && <ul className="checklist" aria-label="What to check">{["Every alignment is Hire", "Every value is scored by someone", "No automatic No Hire", "Every divergence is resolved", "The reference election is recorded"].map(text => <li key={text}><span aria-hidden="true">✓ </span>{text}</li>)}</ul>}
        {dlg?.kind === "outcome" && dlg.value === "pass" && <ReasonCodeField id="outcome-reason-code" value={passCode} onChange={setPassCode} options={codeOptions} />}
      </ReasonDialog>
      <ReasonDialog title={`Resolution — ${dlg?.kind === "divergence" ? dlg.label : ""}`} label="Resolution note" confirm="Resolve" open={dlg?.kind === "divergence"} onClose={() => setDlg(null)} busy={resolve.isPending} error={resolve.error} onConfirm={(resolution_note) => dlg?.kind === "divergence" && void resolve.mutateAsync({ requisition_id: rid, cycle_id: cid, divergence_id: dlg.id, resolution_note }).then(() => setDlg(null))} />
      <ReasonDialog title="Ask for the outcome to be reversed" label="Why" confirm="Ask for a reversal" open={dlg?.kind === "reverse"} onClose={() => setDlg(null)} busy={reverse.isPending || reversalBusy} error={reverse.error} onConfirm={(reason) => dlg?.kind === "reverse" && void reverse.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, reason, source_file_id: reversalFile }).then(() => setDlg(null))}>
        {dlg?.kind === "reverse" && <FileField label="Supporting evidence (optional file)" owner={{ kind: "attachment", owner_type: "requisition", owner_id: rid, requisition_id: rid }} onChange={(file) => setReversalFile(file?.file_id ?? null)} onBusy={setReversalBusy} />}
        <p className="muted">The reason below is required. You may also attach the document supporting it.</p>
        <p className="muted">It takes two signatures: yours and the Recruiter&apos;s, within five business days. After that the outcome stands. It is not available once a verbal offer has gone out.</p>
      </ReasonDialog>
      <Dialog title="Countersign the reversal" open={dlg?.kind === "countersign"} onClose={() => setDlg(null)}>
        <p className="muted">Yours is the second of the two signatures. The outcome is taken back, the candidate returns to the debrief, and everything that outcome started is cancelled with it.</p>
        <label className="check">
          <input type="checkbox" checked={told} onChange={(e) => setTold(e.target.checked)} /> Nothing about this decision has reached the candidate (required)
        </label>
        <Banner error={countersign.error} />
        <div className="row row--end" style={{ gap: 8, marginTop: 8 }}>
          <button type="button" className="btn" onClick={() => setDlg(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" disabled={countersign.isPending || !told} onClick={() => dlg?.kind === "countersign" && void countersign.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, told_check: true }).then(() => setDlg(null))}>
            {countersign.isPending ? "Saving…" : "Countersign"}
          </button>
        </div>
      </Dialog>
      <ReasonDialog title="Take back the request" label="Why" confirm="Take it back" open={dlg?.kind === "withdrawReversal"} onClose={() => setDlg(null)} busy={withdrawReversal.isPending} error={withdrawReversal.error} onConfirm={(reason) => dlg?.kind === "withdrawReversal" && void withdrawReversal.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, reason }).then(() => setDlg(null))}>
        <p className="muted">The outcome you recorded stands exactly as it is.</p>
      </ReasonDialog>
      <RecordForDialog
        open={dlg?.kind === "recordFor"}
        hmName={hmName}
        codes={codeOptions}
        onClose={() => setDlg(null)}
        busy={outcome.isPending}
        error={outcome.error}
        onSubmit={(v) =>
          dlg?.kind === "recordFor" &&
          void outcome
            .mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, outcome: v.value, rationale: v.rationale, for_person: hmId, source: v.source, evidence_ref: v.evidence, happened_at: v.happenedAt, ...(v.value === "pass" && v.reasonCode ? { reason_code: v.reasonCode } : {}) })
            .then(() => setDlg(null))
        }
      />
      <WrittenInputDialog
        open={dlg?.kind === "written"}
        rid={rid}
        offered={offeredOf(c)}
        candidate={dlg?.kind === "written" ? dlg.candidate : ""}
        onClose={() => setDlg(null)}
        busy={written.isPending}
        error={written.error}
        onSubmit={(v) => dlg?.kind === "written" && void written.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, text: v.text, file_id: v.fileId, ...(v.alignment ? { alignment: v.alignment as NonNullable<Parameters<typeof debriefSubmitWrittenInput>[0]["alignment"]> } : {}) }).then(() => setDlg(null))}
      />
      <AddRoundDialog
        open={dlg?.kind === "addRound"}
        onClose={() => setDlg(null)}
        busy={addRound.isPending}
        error={addRound.error}
        onSubmit={(v) => dlg?.kind === "addRound" && void addRound.mutateAsync({ requisition_id: rid, cycle_id: cid, application_id: dlg.aid, interviewer_id: v.interviewer, duration_minutes: v.minutes, format: v.format, values: v.values, ...(v.skills.length ? { skill_numbers: v.skills } : {}), ...(v.label ? { label: v.label } : {}) }).then(() => setDlg(null))}
      />
    </div>
  );
}

// §4.17 — the reason a pass carries onto the disposition worklist. It is picked from the configured dictionary the
// server publishes, and it is left blank when the room named none: the screen never chooses a reason on anyone's behalf.
function ReasonCodeField({ id, value, onChange, options }: { id: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  if (options.length === 0) return null;
  return (
    <Field label="Reason code" id={id} hint="Carried onto the disposition worklist. Leave it blank if the room did not name one.">
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Not stated</option>
        {options.map(([slug, label]) => (
          <option key={slug} value={slug}>
            {label}
          </option>
        ))}
      </select>
    </Field>
  );
}

// §4.15.7 — a Recruiter or Admin writes down the outcome the Hiring Manager gave them; it stays provisional until that
// Hiring Manager confirms it themselves, and it needs to say how it reached them and what the evidence is.
function RecordForDialog({ open, hmName, codes, onClose, onSubmit, busy, error }: { open: boolean; hmName: string; codes: [string, string][]; onClose: () => void; onSubmit: (v: { value: "extend_offer" | "hold_as_alternate" | "additional_round" | "pass"; rationale: string; source: "paper" | "verbal" | "call" | "mail"; evidence: string; happenedAt: string; reasonCode: string }) => void; busy: boolean; error: Parameters<typeof Banner>[0]["error"] }) {
  const [value, setValue] = useState<"extend_offer" | "hold_as_alternate" | "additional_round" | "pass">("extend_offer");
  const [rationale, setRationale] = useState("");
  const [source, setSource] = useState<"paper" | "verbal" | "call" | "mail">("call");
  const [evidence, setEvidence] = useState("");
  const [happened, setHappened] = useState(businessLocalNow);
  const [reasonCode, setReasonCode] = useState("");
  // the control hands back the wall clock the reader typed; it is read in the business zone, the one every timestamp on
  // screen is written in, and a half-typed value resolves to "" — which is what keeps the button disabled.
  const happenedAt = businessLocalToISO(happened);
  useEffect(() => {
    if (!open) return;
    setValue("extend_offer");
    setRationale("");
    setSource("call");
    setEvidence("");
    setHappened(businessLocalNow());
    setReasonCode("");
  }, [open]);
  return (
    <Dialog title={`Record the outcome for ${hmName}`} open={open} onClose={onClose}>
      <p className="muted">It is recorded in their name and waits for them to confirm it. Nothing on the offer track opens until they do.</p>
      <Field label="Outcome" required id="rf-value">
        <select id="rf-value" value={value} onChange={(e) => setValue(e.target.value as "pass")}>
          {(Object.keys(OUTCOME_LABEL) as (keyof typeof OUTCOME_LABEL)[]).map((k) => (
            <option key={k} value={k}>
              {OUTCOME_LABEL[k]}
            </option>
          ))}
        </select>
      </Field>
      {value === "pass" && <ReasonCodeField id="rf-reason-code" value={reasonCode} onChange={setReasonCode} options={codes} />}
      <Field label="Rationale" required id="rf-why">
        <textarea id="rf-why" rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} maxLength={4000} />
      </Field>
      <div className="grid grid--2">
        <Field label="How it reached you" required id="rf-source">
          <select id="rf-source" value={source} onChange={(e) => setSource(e.target.value as "call")}>
            {(["call", "verbal", "mail", "paper"] as const).map((v) => (
              <option key={v} value={v}>
                {SOURCE_LABEL[v]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Evidence" required id="rf-ev" hint="The identifier of the note, message or file this came from — up to 40 characters, not the text of it.">
          <input id="rf-ev" value={evidence} onChange={(e) => setEvidence(e.target.value)} maxLength={40} placeholder="call note 12 Oct" />
        </Field>
      </div>
      <Field label="When they told you" required id="rf-when" hint="The decision counts from then, not from when you typed it. It cannot be in the future.">
        <input id="rf-when" type="datetime-local" max={businessLocalNow()} value={happened} onChange={(e) => setHappened(e.target.value)} />
      </Field>
      <Banner error={error} />
      <div className="row row--end" style={{ gap: 8, marginTop: 8 }}>
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" disabled={busy || rationale.trim().length < 3 || evidence.trim().length < 2 || !happenedAt} onClick={() => onSubmit({ value, rationale: rationale.trim(), source, evidence: evidence.trim(), happenedAt, reasonCode })}>
          {busy ? "Saving…" : "Record for the Hiring Manager"}
        </button>
      </div>
    </Dialog>
  );
}

// §4.15.7 — the additional round for this candidate only: who runs it, what it covers and how long, within the
// touchpoint cap. It produces a new cycle; this one stays exactly as it is.
function AddRoundDialog({ open, onClose, onSubmit, busy, error }: { open: boolean; onClose: () => void; onSubmit: (v: { interviewer: string; minutes: number; format: "virtual" | "in_person"; values: string[]; skills: number[]; label: string }) => void; busy: boolean; error: Parameters<typeof Banner>[0]["error"] }) {
  const [interviewer, setInterviewer] = useState("");
  const [minutes, setMinutes] = useState(45);
  const [format, setFormat] = useState<"virtual" | "in_person">("virtual");
  const [values, setValues] = useState<Record<string, boolean>>({});
  const [skills, setSkills] = useState("");
  const [label, setLabel] = useState("");
  useEffect(() => {
    if (!open) return;
    setInterviewer("");
    setMinutes(45);
    setFormat("virtual");
    setValues({});
    setSkills("");
    setLabel("");
  }, [open]);
  const chosenValues = VALUES.filter((v) => values[v]);
  const chosenSkills = skills
    .split(/[,\s]+/)
    .map((x) => Number(x))
    .filter((n) => Number.isInteger(n) && n > 0);
  return (
    <Dialog title="Run an additional round" open={open} onClose={onClose}>
      <p className="muted">Only this candidate goes back to the panel. A packet is issued for the new round and it is debriefed in a new cycle; this cycle stays as it is.</p>
      <Field label="Interviewer" required id="ar-who" hint="Somebody who can interview and is not already carrying this candidate.">
        <input id="ar-who" value={interviewer} onChange={(e) => setInterviewer(e.target.value)} placeholder="p_int_2" />
      </Field>
      <fieldset className="radios">
        <legend>Values this round covers</legend>
        {VALUES.map((v) => (
          <label key={v} className={`opt${values[v] ? " opt--on" : ""}`}>
            <input type="checkbox" checked={!!values[v]} onChange={(e) => setValues({ ...values, [v]: e.target.checked })} /> {VALUE_NAME[v]}
          </label>
        ))}
      </fieldset>
      <div className="grid grid--2">
        <Field label="Skills" id="ar-skills" hint="Their numbers from the intake, for example 1 2.">
          <input id="ar-skills" value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="1" />
        </Field>
        <Field label="Minutes" required id="ar-min">
          <input id="ar-min" type="number" min={15} max={180} step={15} value={minutes} onChange={(e) => setMinutes(Number(e.target.value) || 45)} />
        </Field>
      </div>
      <div className="grid grid--2">
        <Field label="Format" required id="ar-format">
          <select id="ar-format" value={format} onChange={(e) => setFormat(e.target.value as "virtual")}>
            <option value="virtual">Virtual</option>
            <option value="in_person">In person</option>
          </select>
        </Field>
        <Field label="Name for the round" id="ar-label">
          <input id="ar-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Additional round" />
        </Field>
      </div>
      <Banner error={error} />
      <div className="row row--end" style={{ gap: 8, marginTop: 8 }}>
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" disabled={busy || interviewer.trim().length < 2} onClick={() => onSubmit({ interviewer: interviewer.trim(), minutes, format, values: chosenValues.slice(), skills: chosenSkills, label: label.trim() })}>
          {busy ? "Adding…" : "Add the round"}
        </button>
      </div>
    </Dialog>
  );
}

type Member = DebriefOpenOutput["members"][number];

// Why "Extend an offer" and "Hold as alternate" are not on the table yet, in the room's own words — read from the member
// the room already returns (M07). Pass and an additional round never carry these conditions.
function positiveBlockers(m: Member, hireLike: readonly string[]): string[] {
  const out: string[] = [];
  if (m.withdrawn) out.push("this candidate has withdrawn");
  if (m.divergences_open > 0) out.push(`${m.divergences_open} divergence${m.divergences_open === 1 ? "" : "s"} still ${m.divergences_open === 1 ? "needs" : "need"} a resolution note`);
  // Strong Hire counts as Hire (the room's `level.hire_like`, B14 C4)
  const short = m.alignments.filter((a) => typeof a["value"] !== "string" || !hireLike.includes(a["value"]));
  if (short.length) out.push(`${short.map((a) => String(a["name"])).join(", ")} ${short.length === 1 ? "has" : "have"} not aligned on Hire`);
  if (!m.reference_election) out.push("the reference check election is not recorded");
  return out;
}

function MemberCard({ rid, cid, m, revealed, hmName, offered, hireLike, canRecordFor, canAddRound, canWrite, busy, onAlign, onReference, onOutcome, onRecordFor, onConfirm, onReverse, onCountersign, onWithdrawReversal, onWritten, onAddRound, onNote, onDivergence, canNote, canResolve }: { rid: string; cid: string; m: Member; revealed: boolean; hmName: string; offered: readonly string[]; hireLike: readonly string[]; canRecordFor: boolean; canAddRound: boolean; canWrite: boolean; busy: boolean; onAlign: (v: string) => void; onReference: (elected: boolean) => void; onOutcome: (v: "extend_offer" | "hold_as_alternate" | "additional_round" | "pass") => void; onRecordFor: () => void; onConfirm: () => void; onReverse: () => void; onCountersign: () => void; onWithdrawReversal: () => void; onWritten: () => void; onAddRound: () => void; onNote: () => void; onDivergence: (id: string, label: string) => void; canNote: boolean; canResolve: boolean }) {
  const sheet = useRead(["summary", rid, m.application_id, cid], () => summaryOpen({ requisition_id: rid, application_id: m.application_id, cycle_id: cid }), { enabled: revealed });
  const snap = sheet.data?.snapshot;
  const mine = m.alignments.find((a) => a["mine"]);
  const positive = !!m.can["record_outcome_positive"];
  const negative = !!m.can["record_outcome_negative"];
  const convergingOnPass = m.alignments.length > 0 && m.alignments.every(a => a["value"] === "no_hire");
  const blockers = positiveBlockers(m, hireLike);
  return (
    <section className="card">
      <div className="hrow">
        <h2 className="sec">
          {m.candidate} <Pill category tone="muted">{STAGE_LABEL[m.stage] ?? m.stage}</Pill> <Pill tone={m.sheet_status === "revealed" ? "info" : "warn"}>sheet {m.sheet_status}</Pill>
          {m.withdrawn ? <Pill tone="bad">withdrawn</Pill> : null}
        </h2>
        <div className="acts">
          <a className="btn btn--sm" href={`#requisitions/${rid}/summary/${m.application_id}/${cid}`}>
            Summary Sheet
          </a>
          {canWrite && mine && (
            <button className="btn btn--sm btn--primary" onClick={onWritten}>
              Written input
            </button>
          )}
          {canNote && (
            <button className="btn btn--sm" onClick={onNote}>
              HR note on this candidate
            </button>
          )}
        </div>
      </div>
      {!revealed && (
        <p className="muted">
          Sealed until the debrief opens · scorecards {m.scorecards_in} in · {m.scorecards_due} due · loop {m.loop_status.replace(/_/g, " ")}
        </p>
      )}
      {revealed && snap && <SheetMatrix snap={snap} />}
      {revealed && m.divergences.length > 0 && (
        <>
          <h3 className="sec">Divergences</h3>
          <ul className="plain divs">
            {m.divergences.map((d) => {
              const label = `${dimName(String(d["dimension"]))} · spread ${String(d["spread"])}`;
              return (
                <li key={String(d["id"])} className="row" style={{ gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  {d["resolved"] ? (
                    <>
                      <b>◆ {label}</b>
                      <span>resolved{d["resolution_note"] ? ` — ${String(d["resolution_note"])}` : ""}</span>
                    </>
                  ) : canResolve && !m.outcome ? (
                    <button className="btn btn--sm" onClick={() => onDivergence(String(d["id"]), label)}>
                      ◆ {label} — resolve
                    </button>
                  ) : (
                    <>
                      <b>◆ {label}</b>
                      <span className="muted">the facilitator records the room's resolution</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
      {revealed && (
        <>
          <fieldset className="decision-group"><legend>Your decision</legend>
          <h3 className="sec">Alignment</h3>
          <ul className="plain">
            {m.alignments.map((a) => (
              <li key={String(a["participant_id"])} className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <b style={{ minWidth: 160 }}>{String(a["name"])}</b>
                {a["mine"] && m.can["set_alignment"] ? (
                  <fieldset className="radios" disabled={!!a["bound_no_hire"]}>
                    <legend className="sr-only">Your alignment</legend>
                    {offered.map((v) => (
                      <label key={v} className={`opt${(a["value"] ?? "") === v ? " opt--on" : ""}`}>
                        <input type="radio" name={`al-${m.application_id}`} checked={(a["value"] ?? "") === v} onChange={() => onAlign(v)} /> {recoLabel(v)}
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <Pill tone={recoTone(a["value"])}>{a["value"] ? recoLabel(a["value"]) ?? String(a["value"]) : "missing"}</Pill>
                )}
                {snap && (snap["rows"] as Record<string, unknown>[] | undefined)?.filter(row => row["author"] === a["name"]).map((row, index) => <span key={index} className="muted">submitted: {recoLabel(row["recommendation"]) ?? "Not recorded"}</span>)}
                {a["bound_no_hire"] ? <span className="muted">bound by the two-2s rule</span> : null}
                {/* POLICY §6.2 (Codex #9 M5): written input is EVIDENCE, never a binding alignment. The row stays
                    missing until they set it themselves, and the server's own label says why it is missing — the room
                    must not read it as an oversight. `source === "written_input"` was the old behaviour and is gone. */}
                {a["written_input"] ? (
                  <span className="muted">
                    {String(a["written_input_label"] ?? "written input — not an alignment")}
                    {a["written_input_says"] ? ` · they would have said ${recoLabel(a["written_input_says"]) ?? String(a["written_input_says"])}` : ""}
                  </span>
                ) : null}
                {a["written_input_text"] ? <p className="roll__input">{String(a["written_input_text"])}</p> : null}
              </li>
            ))}
          </ul>
          <div className="row" role="group" aria-label="Reference check" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
            {m.reference_election ? (
              <>
                <Pill tone="muted">reference check: {m.reference_election["elected"] ? "yes" : "no"}</Pill>
                {m.reference_election["text"] ? <span className="muted">{String(m.reference_election["text"])}</span> : null}
              </>
            ) : m.can["record_reference_decision"] ? (
              <>
                <span className="muted">Reference check?</span>
                <button className="btn btn--sm" onClick={() => onReference(true)}>
                  Yes
                </button>
                <button className="btn btn--sm" onClick={() => onReference(false)}>
                  No
                </button>
              </>
            ) : (
              <span className="muted">Reference check: pending the Hiring Manager</span>
            )}
          </div>
          {m.outcome ? (
            <div className="outcome decision-receipt">
              <h3> {OUTCOME_LABEL[String(m.outcome["value"])] ?? String(m.outcome["value"]).replace(/_/g, " ")} <span className="decision-receipt__by">Recorded by {String(m.outcome["by"])} · {fmtDate(String(m.outcome["at"]), true)}</span>{" "}
                {m.outcome["provisional"] ? <Pill tone="warn">provisional</Pill> : <Pill tone="ok">recorded</Pill>}
              </h3>
              {m.outcome["provisional"] ? (
                <p className="outcome__meta">
                  {String(m.outcome["status_text"] ?? "provisional — awaiting the Hiring Manager's confirmation")}
                  {m.outcome["entered_by"] ? ` · written down by ${String(m.outcome["entered_by"])}` : ""}
                  {m.outcome["source"] ? ` · it reached them ${SOURCE_LABEL[String(m.outcome["source"])] ?? String(m.outcome["source"])}` : ""}
                </p>
              ) : null}
              {m.outcome["rationale"] ? <blockquote className="prose">{String(m.outcome["rationale"])}</blockquote> : null}
              <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                {m.can["confirm_outcome"] && (
                  <button className="btn btn--primary" disabled={busy} onClick={onConfirm}>
                    Confirm the outcome
                  </button>
                )}
                {m.can["reverse_outcome"] && (
                  <button className="btn" onClick={onReverse}>
                    Reverse the outcome
                  </button>
                )}
                {canAddRound && String(m.outcome["value"]) === "additional_round" && (
                  <button className="btn" onClick={onAddRound}>
                    Add the extra round
                  </button>
                )}
              </div>
              {m.reversal ? (
                <div className="twosig">
                  <h4>A reversal is waiting for a second signature</h4>
                  <p className="outcome__meta">
                    Asked for by {String(m.reversal["requested_by"])} · {fmtDate(String(m.reversal["requested_at"]), true)}
                    {m.reversal["due_at"] ? ` · the Recruiter has until ${fmtDate(String(m.reversal["due_at"]), true)}, after which the outcome stands` : ""}
                  </p>
                  {m.reversal["reason"] ? <p className="prose">{String(m.reversal["reason"])}</p> : null}
                  <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                    {m.can["countersign_reversal"] && (
                      <button className="btn btn--primary" disabled={busy} onClick={onCountersign}>
                        Countersign
                      </button>
                    )}
                    {m.can["withdraw_reversal"] && (
                      <button className="btn" onClick={onWithdrawReversal}>
                        Take the request back
                      </button>
                    )}
                    {!m.can["countersign_reversal"] && !m.can["withdraw_reversal"] && <span className="muted">Waiting on {String(m.reversal["awaiting"])}.</span>}
                  </div>
                </div>
              ) : null}
            </div>
          ) : positive || negative ? (
            <>
              <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {(positive || negative) && (
                  <>
                    <button className="btn btn--primary" disabled={!positive || busy} aria-describedby={!positive ? `decision-blockers-${m.application_id}` : undefined} onClick={() => onOutcome("extend_offer")}>
                      Extend an offer
                    </button>
                    <button className="btn" disabled={!positive || busy} aria-describedby={!positive ? `decision-blockers-${m.application_id}` : undefined} onClick={() => onOutcome("hold_as_alternate")}>
                      Hold as alternate
                    </button>
                  </>
                )}
                {negative && (
                  <>
                    <button className="btn" onClick={() => onOutcome("additional_round")}>
                      Run an additional round
                    </button>
                    <button className={`btn${convergingOnPass ? " btn--primary" : ""}`} onClick={() => onOutcome("pass")}>
                      Pass
                    </button>
                  </>
                )}
              </div>
              {!positive && (
                <p className="muted" id={`decision-blockers-${m.application_id}`}>
                  {blockers.length ? `An offer or an alternate hold is not available yet: ${blockers.join("; ")}.` : "An offer or an alternate hold is not available on this search right now."}
                </p>
              )}
            </>
          ) : (
            // a withdrawn candidate has no outcome to wait for; everyone else is waiting on the Hiring Manager, and
            // whoever took the outcome down from them may write it in their name (§4.15.7)
            <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
              <span className="muted">{m.withdrawn ? "No outcome is recorded — the candidate withdrew." : m.divergences_open > 0 ? `${m.divergences_open} divergence(s) need a resolution note before the outcome.` : `Outcome: pending ${hmName}.`}</span>
              {canRecordFor && !m.withdrawn && (
                <button className="btn btn--sm" onClick={onRecordFor}>
                  Record for the Hiring Manager
                </button>
              )}
            </div>
          )}
          </fieldset>
        </>
      )}
    </section>
  );
}

// ---- the sheet matrix -------------------------------------------------------------------------------------------------------------

function SheetMatrix({ snap }: { snap: Record<string, unknown> }) {
  const labels = snap["dimension_labels"] as Record<string, string> | undefined;
  const dimName = (dimension: string) => labels?.[dimension] ?? (dimension.startsWith("skill_") ? `Skill ${dimension.slice(6)} (name not recorded)` : VALUE_NAME[dimension] ?? dimension);
  const rows = (snap["rows"] as Record<string, unknown>[] | undefined) ?? [];
  const uniformStatus = rows.length > 0 && rows.every(row => row["status"] === rows[0]?.["status"]);
  const dims = Array.from(new Set(rows.flatMap((r) => Object.keys((r["ratings"] as Record<string, unknown>) ?? {}))));
  const order = ["respect", "ownership", "opportunity", "together", "service"];
  dims.sort((a, b) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)) || a.localeCompare(b));
  const div = ((snap["divergences"] as Record<string, unknown>[] | undefined) ?? []).map((d) => String(d["dimension"]));
  const flags = (snap["flags"] as Record<string, unknown> | undefined) ?? {};
  const exceptions = (snap["exceptions"] as Record<string, unknown>[] | undefined) ?? [];
  const reservations = rows.filter((r) => r["reservation"]);
  const otherRole = rows.filter((r) => (r["consider_other_role"] as Record<string, unknown> | null)?.["answer"] === "yes");
  const notAssessed = rows.flatMap((r) =>
    Object.entries((r["ratings"] as Record<string, Record<string, unknown>>) ?? {})
      .filter(([, cell]) => cell["state"] === "not_assessed")
      .map(([d, cell]) => ({ key: `${String(r["scorecard_id"])}-${d}`, author: String(r["author"]), dim: dimName(d), reason: String(cell["not_assessed_reason"] ?? "no reason recorded") })),
  );
  return (
    <div className="sheet">
      <Table
        caption={uniformStatus ? `All ${rows.length} scorecards ${String(rows[0]?.["status"]).replace(/_/g, " ")}` : undefined}
        label="Summary sheet"
        head={
          <tr>
            <th className="sticky-l">Interviewer</th>
            {dims.map((d) => (
              <th key={d} className={div.includes(d) ? "diverge" : ""}>
                <abbr title={dimName(d)} aria-hidden="true">{dimShort(d)}</abbr>
                <span className="sr-only">{dimName(d)}</span>
              </th>
            ))}
            <th>AVG</th>
            <th>Recommendation</th>
            {!uniformStatus && <th>Status</th>}
          </tr>
        }
      >
        {rows.map((r) => {
          const ratings = (r["ratings"] as Record<string, Record<string, unknown>>) ?? {};
          return (
            <tr key={String(r["scorecard_id"])} className={r["is_screen"] ? "muted" : ""}>
              <td className="sticky-l">
                <b>{String(r["author"])}</b>
                <div className="muted nowrap">{String(r["round"])}</div>
              </td>
              {dims.map((d) => {
                const cell = ratings[d];
                if (!cell) return <td key={d} className="num"><NA /></td>;
                if (cell["state"] === "not_assessed")
                  return (
                    <td key={d} className="num">
                      <NA reason={`Not assessed — ${dimName(d)}: ${String(cell["not_assessed_reason"] ?? "no reason recorded")}`} />
                    </td>
                  );
                return (
                  <td key={d} className={`num ${cell["flag_low"] ? "low" : div.includes(d) ? "diverge" : ""}`}>
                    {String(cell["score"])}
                    {cell["flag_low"] ? " ▲" : div.includes(d) ? " ◆" : ""}
                  </td>
                );
              })}
              {/* B14 (C3): a playbook sheet's row average is the card's average core score (values only); a sheet
                  frozen before B14 keeps the average it was compiled with */}
              <td className="num">{"core_average" in r ? (r["core_average"] == null ? <NA /> : String(r["core_average"])) : r["average"] == null ? <NA /> : String(r["average"])}</td>
              <td>
                {r["recommendation"] ? recoLabel(r["recommendation"]) ?? String(r["recommendation"]) : <NA />}
                {r["two_2s"] ? <Pill tone="bad">automatic No Hire</Pill> : null}
              </td>
              {!uniformStatus && <td className="muted long">{String(r["status"])}</td>}
            </tr>
          );
        })}
      </Table>
      <p className="muted">{dims.map(d => `${dimShort(d).toUpperCase()} ${dimName(d)}`).join(" · ")}</p>
      {flags["code_of_conduct_status_only"] ? <p className="callout" role="status">A Code of Conduct concern exists — waiting on the HR Business Partner.</p> : null}
      <p className="muted">
        {"core_average" in snap
          ? `Average core score ${snap["core_average"] == null ? "—" : `${String(snap["core_average"])} / 5`} (${String(snap["core_values_scored"] ?? 0)} of 5 values)${snap["core_band"] && typeof snap["core_band"] === "object" ? ` · ${String((snap["core_band"] as Record<string, unknown>)["text"] ?? "")}` : ""}`
          : `Panel average ${String(snap["panel_average"] ?? "—")}`} · unassessed: {((snap["unassessed"] as string[]) ?? []).map((v) => VALUE_NAME[v] ?? v).join(", ") || "none"}
        {flags["single_weak_score"] ? ` · ${String(flags["single_weak_score"])} single weak score(s) — worth discussing` : ""}
        {flags["recorded_from_paper_unconfirmed"] ? " · a scorecard recorded from paper is unconfirmed" : ""}
      </p>
      {reservations.length > 0 && (
        <div className="sheet__notes">
          <h3 className="sub">Reservations</h3>
          <ul className="plain">
            {reservations.map((r) => (
              <li key={`res-${String(r["scorecard_id"])}`}>
                <b>{String(r["author"])}</b> <span className="muted">({String(r["round"])})</span> — {String(r["reservation"])}
              </li>
            ))}
          </ul>
        </div>
      )}
      {otherRole.length > 0 && (
        <div className="sheet__notes">
          <h3 className="sub">Considered for another role</h3>
          <ul className="plain">
            {otherRole.map((r) => (
              <li key={`cor-${String(r["scorecard_id"])}`}>
                <b>{String(r["author"])}</b> <span className="muted">({String(r["round"])})</span> — {String((r["consider_other_role"] as Record<string, unknown>)["suggested_role"] ?? "another role")}
              </li>
            ))}
          </ul>
        </div>
      )}
      {notAssessed.length > 0 && (
        <div className="sheet__notes">
          <h3 className="sub">Not assessed</h3>
          <ul className="plain">
            {notAssessed.map((n) => (
              <li key={n.key}>
                <b>{n.author}</b> <span className="muted">({n.dim})</span> — {n.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {exceptions.length > 0 && (
        <div className="sheet__notes">
          <h3 className="sub">Exceptions on the record</h3>
          <ul className="plain">
            {exceptions.map((e) => (
              <li key={String(e["id"])}>
                <Pill tone="warn">{String(e["kind"]).replace(/_/g, " ")}</Pill> {String(e["reason"] ?? "")}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---- #requisitions/<R>/summary/<cand>[/<dc>] ------------------------------------------------------------------------------------------

export function SummarySheetPage({ rid, aid, cid }: { rid: string; aid: string; cid?: string | undefined }) {
  const { register } = useViewState();
  const ref = { requisition_id: rid, application_id: aid, ...(cid ? { cycle_id: cid } : {}) };
  const q = useRead(["summary", rid, aid, cid ?? ""], () => summaryOpen(ref));
  const rd = useRead(["summary-readiness", rid, aid, cid ?? ""], () => summaryReadinessCheck(ref), { retry: false });
  // the room's own record for this candidate — the divergence resolutions and the outcome the debrief reached. Read
  // from the room, never re-derived here; a reader without the room simply does not get this section. The room is a
  // different seat from the sheet, and every refusal is a receipt in the ledger, so this read only fires once the sheet
  // is revealed — the state in which the room has a record to show — and is never retried.
  const cycleId = cid ?? q.data?.cycle_id ?? "";
  const room = useRead(["debrief", cycleId, rid], () => debriefOpen({ requisition_id: rid, cycle_id: cycleId }), { enabled: !!cycleId && q.data?.status === "revealed", retry: false });
  useEffect(() => register({ view: "summary", ids: { requisition: rid, application: aid }, summary: `summary ${q.data?.status ?? ""}` }), [register, rid, aid, q.data?.status]);
  const inval = [["summary", rid, aid, cid ?? ""], ["summary-readiness", rid, aid, cid ?? ""], ["debrief"], ["today"]] as const;
  const compile = useWrite((i: { requisition_id: string; application_id: string }, k) => summaryCompile(i, k), inval);
  const exception = useWrite((i: Parameters<typeof summaryRecordException>[0], k) => summaryRecordException(i, k), inval);
  const [dlg, setDlg] = useState<null | "exception" | "export">(null);
  const [kind, setKind] = useState<string>("scorecard_missing_at_reveal");
  const [interview, setInterview] = useState("");
  const [rendition, setRendition] = useState<SavedRendition | null>(null);
  const [exportErr, setExportErr] = useState<AppError | Error | null>(null);
  const [exporting, setExporting] = useState(false);
  if (q.error || !q.data) return <ObjectReadGuard query={q} />;
  const s = q.data;
  // AF-22 (EM-20): the Summary Sheet is not the interviewer's screen, sealed or revealed. The server names their seat
  // `own` at every moment (POLICY §3.1 P-SUM) — before the reveal it carries the sealed status and no snapshot, after it
  // their own frozen row — so the page resolves to §3.3 on the server's word, never on a sealed status it would have to
  // guess from, and the control the debrief no longer offers them cannot be reached by its route either. Their own
  // scorecard is on their debrief and in My Interviews.
  if (s.projection === "own") return <NotFound route={`requisitions/${rid}/summary/${aid}${cid ? `/${cid}` : ""}`} />;
  const member = room.data?.members.find((m) => m.application_id === aid);
  const resolved = (member?.divergences ?? []).filter((d) => d["resolved"]);
  const runExport = () => {
    setExportErr(null);
    setExporting(true);
    void summaryExport(ref)
      .then((r) => {
        setRendition(r);
        setDlg("export");
      })
      .catch((e: unknown) => setExportErr(e instanceof Error ? e : new Error(String(e))))
      .finally(() => setExporting(false));
  };
  return (
    <div className="screen-only"><RecordAfterFact id={rid} family="requisition" section="summary" /><p className="paper-only">Use Export after the reveal to print a saved Summary Sheet with its export receipt.</p>
      <div className="hrow">
        <div>
          <h1 className="page">Summary Sheet · {s.candidate}</h1>
          <p className="lede">
            <a href={`#requisitions/${rid}/candidates/${aid}`}>← application</a> · {s.projection === "compiler" ? "your compile view" : s.status === "revealed" ? "revealed" : "sealed until the debrief opens"} · scorecards {s.scorecards_in} in · {s.scorecards_due} due
          </p>
        </div>
        <div className="acts">
          <Pill tone={s.status === "revealed" ? "info" : s.status === "sealed" ? "warn" : "muted"}>{s.status}</Pill>
          {s.can["compile"] && (
            <button className="btn btn--primary" disabled={compile.isPending || !(rd.data?.ready ?? false)} title={rd.data && !rd.data.ready ? "Readiness gaps remain — record an exception for each" : ""} onClick={() => void compile.mutateAsync({ requisition_id: rid, application_id: aid })}>
              {s.compiled_at ? "Recompile" : "Compile"}
            </button>
          )}
          {s.can["record_exception"] && (
            <button className="btn" onClick={() => setDlg("exception")}>
              Record an exception
            </button>
          )}
          {s.can["export"] && (
            <button className="btn" disabled={exporting} onClick={runExport}>
              Export
            </button>
          )}
        </div>
      </div>
      <Banner error={compile.error ?? exception.error ?? exportErr} />
      {compile.isSuccess && <Receipt>Compiled — sealed · reveal happens in the debrief room.</Receipt>}
      {rd.data && (
        <section className="card">
          <h2 className="sec">Readiness</h2>
          {/* the six short facts flow in the KV grid's columns; EXCEPTIONS is prose — a kind and the reason someone typed —
              and in one 160px column it wrapped over eight lines beside two empty ones (UAT 02 #23), so it takes the last
              row whole. The grid is written out here rather than passed to <KV> because only a grid CHILD can span it; the
              span uses the shared kv__wide rule. */}
          <dl className="kv">
            {(
              [
                ["Loop", rd.data.loop_status.replace(/_/g, " ")],
                ["Rounds not held", rd.data.rounds_not_held.length ? rd.data.rounds_not_held.map((x) => `${String(x["round"])} (${String(x["reason"])})`).join(" · ") : "none"],
                ["Scorecards outstanding", rd.data.scorecards_outstanding.length ? rd.data.scorecards_outstanding.map((x) => `${String(x["round"])} — ${String(x["author"])}`).join(" · ") : "none"],
                ["Recorded on paper, unconfirmed", rd.data.evidence_unconfirmed.length ? rd.data.evidence_unconfirmed.map((x) => `${String(x["round"])} — ${String(x["author"])}`).join(" · ") : "none"],
                ["Values uncovered", rd.data.values_uncovered.join(", ") || "none"],
                ["Concerns", rd.data.concerns_open ? <Pill tone="bad">a concern exists — waiting on the HR Business Partner</Pill> : "none"],
              ] as [string, ReactNode][]
            ).map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
            <div className="kv__wide">
              <dt>Exceptions</dt>
              <dd>{rd.data.exceptions.length ? rd.data.exceptions.map((e) => `${String(e["kind"]).replace(/_/g, " ")} — ${String(e["reason"] ?? "")}`).join(" · ") : "none"}</dd>
            </div>
          </dl>
          {rd.data.gaps.length > 0 && (
            <ul className="plain gaps">
              {rd.data.gaps.map((g) => (
                <li key={g}>{gapText(g)}</li>
              ))}
            </ul>
          )}
        </section>
      )}
      {s.snapshot ? <SheetMatrix snap={s.snapshot} /> : <Empty level={2} heading="Summary is sealed">Sealed until the debrief opens · scorecards {s.scorecards_in} in · {s.scorecards_due} due.</Empty>}
      {member && (resolved.length > 0 || member.outcome) && (
        <section className="card">
          <h2 className="sec">In the room</h2>
          {resolved.length > 0 && (
            <ul className="plain divs">
              {resolved.map((d) => (
                <li key={String(d["id"])}>
                  <b>◆ {dimName(String(d["dimension"]))} · spread {String(d["spread"])}</b> — resolved{d["resolution_note"] ? ` — ${String(d["resolution_note"])}` : ""}
                </li>
              ))}
            </ul>
          )}
          {member.outcome && (
            <div className="outcome decision-receipt">
              <h3> {OUTCOME_LABEL[String(member.outcome["value"])] ?? String(member.outcome["value"]).replace(/_/g, " ")} <span className="muted">· {String(member.outcome["by"])} · {fmtDate(String(member.outcome["at"]), true)}</span>
              </h3>
              {member.outcome["rationale"] ? <p className="prose">{String(member.outcome["rationale"])}</p> : null}
            </div>
          )}
        </section>
      )}
      <Dialog title="Record an exception" open={dlg === "exception"} onClose={() => setDlg(null)}>
        <p className="muted">Passes exactly one process gate before the reveal and prints on the sheet. Hard rules (two 2s, alignment, five values, no override) are never exceptable.</p>
        <Field label="Kind" required id="ex-kind">
          <select id="ex-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            {EXCEPTIONS.map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        {(kind === "scorecard_missing_at_reveal" || kind === "round_not_held_at_reveal") && (
          <Field label="Round (interview id)" required id="ex-iv" hint="From the readiness list">
            <select id="ex-iv" value={interview} onChange={(e) => setInterview(e.target.value)}>
              <option value="">—</option>
              {(rd.data?.rounds_not_held ?? []).map((x) => (
                <option key={String(x["interview_id"])} value={String(x["interview_id"])}>
                  {String(x["round"])} — {String(x["reason"])}
                </option>
              ))}
              {(rd.data?.scorecards_outstanding ?? []).map((x) => (
                <option key={String(x["scorecard_id"])} value={String(x["interview_id"] ?? "")}>
                  {String(x["round"])} — {String(x["author"])}
                </option>
              ))}
              {(rd.data?.evidence_unconfirmed ?? []).map((x) => (
                <option key={`unconfirmed-${String(x["scorecard_id"])}`} value={String(x["interview_id"] ?? "")}>
                  {String(x["round"])} — {String(x["author"])} (recorded from paper, not confirmed)
                </option>
              ))}
            </select>
          </Field>
        )}
        <ReasonInline busy={exception.isPending} onConfirm={(reason) => void exception.mutateAsync({ requisition_id: rid, kind: kind as "exercise_waived", reason, application_id: aid, ...(cid ? { cycle_id: cid } : {}), ...(interview ? { interview_id: interview } : {}) }).then(() => setDlg(null))} onCancel={() => setDlg(null)} />
      </Dialog>
      <SavedPrint title="Export — Confidential, Internal Use Only" value={dlg === "export" ? rendition : null} kind="summary" onClose={() => setDlg(null)} />
    </div>
  );
}

function ReasonInline({ busy, onConfirm, onCancel }: { busy: boolean; onConfirm: (reason: string) => void; onCancel: () => void }) {
  const [reason, setReason] = useState("");
  return (
    <>
      <Field label="Reason" required id="ex-reason">
        <textarea id="ex-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="acts">
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn--primary" disabled={busy || reason.trim().length < 3} onClick={() => onConfirm(reason.trim())}>
          Record
        </button>
      </div>
    </>
  );
}
